/**
 * Les lignes calculées : créances (400000) et crédits (460000) des familles
 * au 31/12/2025, et leur recoupement avec le grand livre des familles.
 *
 * ## Valider en lot, trancher une par une
 *
 * Une ligne sans remarque se valide à son montant calculé, en lot. Une ligne
 * « à revoir » (sortie non couverte, ajustements qui dépassent la créance,
 * notes à trancher, écart avec le grand livre) se tranche seule, justifiée.
 *
 * ## Le contrôle clients bloque
 *
 * Par famille, la reprise (400000 − 460000) doit égaler le grand livre des
 * familles à l'arrêté, diminué des notes retenues pour l'ouverture. Un écart
 * empêche la validation : on ne corrige pas une créance d'ouverture en
 * s'écartant de l'auxiliaire. Une créance jugée irrécouvrable au 31/12/2025
 * relève d'une réduction de valeur (409000, « Autres actifs », au crédit) —
 * `À VALIDER COMPTABLE`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import {
  lireLesLignes,
  proposerLaRepriseClients,
  validerLesLignesCalculees,
  validerUneLigne,
} from '../../../lib/compta/reprise/gateway.supabase';
import { ETATS_LIGNE, SENS, type EtatLigne, type LigneDeReprise } from '../../../lib/compta/reprise/types';
import SaisieMontant from '../components/SaisieMontant';
import TraitementCreance from './TraitementCreance';
import { euros } from '../components/money';
import type { EtatDeLaReprise } from '../AdminRepriseOuverturePage';
import { financesDuCompte } from '../../../lib/navigation/chemins';

const PAR_PAGE = 50;
type Filtre = 'a_traiter' | 'a_revoir' | 'toutes';
const ETATS_DU_FILTRE: Record<Filtre, EtatLigne[]> = {
  a_traiter: ['a_revoir', 'a_valider'],
  a_revoir: ['a_revoir'],
  toutes: [],
};

export default function LignesClients({ etat, apresGeste, version }: {
  etat: EtatDeLaReprise; apresGeste: () => void; version: number;
}) {
  const { reprise, synthese, ecartsClients } = etat;
  const modifiable = reprise.statut === 'brouillon';
  const [filtre, setFiltre] = useState<Filtre>('a_traiter');
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(0);
  const [lignes, setLignes] = useState<LigneDeReprise[]>([]);
  const [total, setTotal] = useState(0);
  const [chargement, setChargement] = useState(false);
  const [enCours, setEnCours] = useState<null | 'proposer' | 'lot'>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);

  /* Les compteurs d'etat ne lisent que les lignes calculees et l'effet
     « solde » de chacune : une creance douteuse ne se compte pas deux fois. */
  const aValiderEnLot = useMemo(() => synthese.reduce((t, s) => t + s.nbAValider, 0), [synthese]);
  const calculees = useMemo(() => synthese.reduce((t, s) => t + s.nbCalculees, 0), [synthese]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await lireLesLignes(reprise.id, { origine: 'calcul', etats: ETATS_DU_FILTRE[filtre], recherche }, page, PAR_PAGE);
      setLignes(r.lignes);
      setTotal(r.total);
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setChargement(false);
    }
  }, [reprise.id, filtre, recherche, page]);

  useEffect(() => { void charger(); }, [charger, version]);
  useEffect(() => { setPage(0); }, [filtre, recherche]);

  const proposer = async () => {
    setEnCours('proposer');
    try {
      await proposerLaRepriseClients(reprise.id);
      toast.success('Proposition recalculée depuis les faits au 31/12/2025');
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e), { duration: 8000 });
    } finally {
      setEnCours(null);
    }
  };

  const validerEnLot = async () => {
    setEnCours('lot');
    try {
      const n = await validerLesLignesCalculees(reprise.id);
      toast.success(`${n} ligne(s) validée(s) à leur montant calculé`);
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e), { duration: 8000 });
    } finally {
      setEnCours(null);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAR_PAGE));

  return (
    <div className="space-y-6">
      {modifiable && (
        <section className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <button type="button" onClick={proposer} disabled={enCours !== null}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {enCours === 'proposer' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {calculees === 0 ? 'Calculer la proposition' : 'Recalculer la proposition'}
          </button>
          <button type="button" onClick={validerEnLot} disabled={enCours !== null || aValiderEnLot === 0}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
            {enCours === 'lot' && <Loader2 className="h-4 w-4 animate-spin" />}
            Valider les {aValiderEnLot} ligne(s) sans remarque
          </button>
          <p className="text-xs text-gray-500">
            Recalculer ne touche pas aux décisions humaines sur les notes ; une ligne dont le calcul change perd
            sa validation.
          </p>
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-base font-medium text-gray-900">
          Contrôle avec le grand livre des familles
          {ecartsClients.length === 0
            ? <span className="ml-2 text-sm font-normal text-emerald-700">concordant</span>
            : <span className="ml-2 text-sm font-normal text-amber-700">{ecartsClients.length} écart(s)</span>}
        </h2>
        {ecartsClients.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-amber-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2">Tiers client</th>
                  <th className="px-3 py-2 text-right">Grand livre</th>
                  <th className="px-3 py-2 text-right">Notes d’ouverture</th>
                  <th className="px-3 py-2 text-right">Éteintes</th>
                  <th className="px-3 py-2 text-right">Attendu</th>
                  <th className="px-3 py-2 text-right">Reprise</th>
                  <th className="px-3 py-2 text-right">Écart</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ecartsClients.map((x) => (
                  <tr key={x.tiersId ?? x.userId ?? x.nom ?? ''}>
                    <td className="px-3 py-1.5">
                      {x.userId
                        ? <Link to={financesDuCompte(x.userId)} className="text-indigo-700 hover:underline">
                            {x.nom ?? x.userId.slice(0, 8)}
                          </Link>
                        : (x.nom ?? '—')}
                      <span className="ml-1 text-xs text-gray-500">{x.segment === 'famille' ? 'famille CDV' : 'hors CDV'}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{euros(x.grandLivreCents)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{euros(x.notesCents)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{x.eteintesCents ? euros(x.eteintesCents) : ''}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{euros(x.attenduCents)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{euros(x.repriseCents)}</td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums text-amber-700">{euros(x.ecartCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-gray-500">
          Par tiers client, net des créances (400000, et 407000 pour une part douteuse) et des acomptes (46). Une famille
          CDV se recoupe avec le grand livre des familles, moins les notes d’ouverture et les créances éteintes ; un
          client hors CDV est justifié par sa saisie. Une note de transition à trancher y apparaît tant qu’elle ne l’est pas.
        </p>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1 text-sm">
            {(['a_traiter', 'a_revoir', 'toutes'] as const).map((f) => (
              <button key={f} type="button" onClick={() => setFiltre(f)}
                className={clsx('rounded-md px-3 py-1', filtre === f ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')}>
                {f === 'a_traiter' ? 'À traiter' : f === 'a_revoir' ? 'À revoir' : 'Toutes'}
              </button>
            ))}
          </div>
          <input value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Nom de la famille"
            className="w-60 rounded border border-gray-300 px-2 py-1 text-sm" />
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Famille</th>
                <th className="px-3 py-2">Compte</th>
                <th className="px-3 py-2 text-right">Calculé</th>
                <th className="px-3 py-2 text-right">Validé</th>
                <th className="px-3 py-2">État</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {chargement && lignes.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>
              )}
              {!chargement && lignes.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                  {calculees === 0 ? 'Aucune ligne calculée : lancer la proposition.' : 'Rien à traiter ici.'}
                </td></tr>
              )}
              {lignes.map((l) => (
                <LigneCalculee key={l.id} ligne={l} ouverte={ouverte === l.id} modifiable={modifiable}
                  basculer={() => setOuverte(ouverte === l.id ? null : l.id)}
                  apresGeste={() => { setOuverte(null); apresGeste(); }} />
              ))}
            </tbody>
          </table>
        </div>
        {total > PAR_PAGE && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>{total} ligne(s) — page {page + 1} / {pages}</span>
            <div className="flex gap-2">
              <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}
                className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Précédente</button>
              <button type="button" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}
                className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40">Suivante</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function LigneCalculee({ ligne, ouverte, modifiable, basculer, apresGeste }: {
  ligne: LigneDeReprise; ouverte: boolean; modifiable: boolean; basculer: () => void; apresGeste: () => void;
}) {
  const [montant, setMontant] = useState(ligne.montantValideCents ?? ligne.montantCalculeCents ?? 0);
  const [justification, setJustification] = useState(ligne.justification ?? '');
  const [envoi, setEnvoi] = useState(false);
  const justificationRequise = ligne.aRevoir !== null || montant !== ligne.montantCalculeCents;

  const valider = async () => {
    setEnvoi(true);
    try {
      await validerUneLigne(ligne.id, montant, justification);
      toast.success('Ligne validée');
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e), { duration: 8000 });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <>
      <tr>
        <td className="px-3 py-1.5">
          {ligne.userId
            ? <Link to={financesDuCompte(ligne.userId)} className="text-indigo-700 hover:underline">{ligne.tiersNom ?? '—'}</Link>
            : (ligne.tiersNom ?? '—')}
          {ligne.aRevoir && <div className="text-xs text-red-700">{ligne.aRevoir}</div>}
          {(ligne.douteuxCents > 0 || ligne.eteintCents > 0) && (
            <div className="text-xs text-amber-800">
              {ligne.douteuxCents > 0 && <>douteuse {euros(ligne.douteuxCents)} (réduction {euros(ligne.reductionValeurCents)}) </>}
              {ligne.eteintCents > 0 && <>éteinte {euros(ligne.eteintCents)}</>}
            </div>
          )}
        </td>
        <td className="px-3 py-1.5 text-gray-700">{ligne.compteCode} · {SENS[ligne.sens]}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{ligne.montantCalculeCents === null ? '—' : euros(ligne.montantCalculeCents)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{ligne.montantValideCents === null ? '—' : euros(ligne.montantValideCents)}</td>
        <td className="px-3 py-1.5">
          <span className={clsx('rounded-full px-2 py-0.5 text-xs', ETATS_LIGNE[ligne.etat].classe)}>{ETATS_LIGNE[ligne.etat].label}</span>
        </td>
        <td className="px-3 py-1.5 text-right">
          {modifiable && (
            <button type="button" onClick={basculer} className="text-sm text-indigo-700 hover:underline">
              {ouverte ? 'Fermer' : ligne.etat === 'validee' ? 'Revoir' : 'Trancher'}
            </button>
          )}
        </td>
      </tr>
      {ouverte && ligne.compteCode === '400000' && ligne.sens === 'debit' && (
        <tr className="bg-indigo-50/40">
          <td colSpan={6} className="px-3 pb-3 pt-2">
            <TraitementCreance ligne={ligne} apresGeste={apresGeste} montantModifiable />
          </td>
        </tr>
      )}
      {ouverte && !(ligne.compteCode === '400000' && ligne.sens === 'debit') && (
        <tr className="bg-indigo-50/40">
          <td colSpan={6} className="space-y-2 px-3 pb-3 pt-2">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-2">Montant validé
                <SaisieMontant valeurCents={montant} onChange={setMontant} className="w-32" ariaLabel="Montant validé" />
              </label>
              {ligne.montantCalculeCents !== null && montant !== ligne.montantCalculeCents && (
                <button type="button" className="text-xs text-indigo-700 hover:underline"
                  onClick={() => setMontant(ligne.montantCalculeCents ?? 0)}>
                  revenir au calcul ({euros(ligne.montantCalculeCents)})
                </button>
              )}
            </div>
            {ligne.montantCalculeCents !== null && montant !== ligne.montantCalculeCents && (
              <p className="text-xs text-amber-800">
                S’écarter du calcul fera apparaître un écart avec le grand livre des familles, qui bloque la validation.
              </p>
            )}
            <label className="block text-sm">
              Justification{justificationRequise && <span className="text-red-600"> (obligatoire)</span>}
              <textarea value={justification} rows={2} onChange={(e) => setJustification(e.target.value)}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm" />
            </label>
            <button type="button" onClick={valider} disabled={envoi || (justificationRequise && justification.trim() === '')}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {envoi && <Loader2 className="h-4 w-4 animate-spin" />} Valider
            </button>
          </td>
        </tr>
      )}
    </>
  );
}
